/**
 * Canonical cart button state resolver.
 *
 * Single source of truth for the initial "add to cart" button state. Used by:
 * sections/main-product.liquid inline boot, sections/featured-product.liquid
 * inline boot, assets/variant-picker.js, snippets/sticky-atc.liquid /
 * assets/sticky-atc.js.
 *
 * Ownership split with the RuffRuff Pre-order app:
 * 1. When a variant has an in-effect RuffRuff pre-order rule (within its
 *    startsAt / endsAt window), the button is owned by RuffRuff. The theme
 *    reveals an enabled pre-order button once and then leaves it alone --
 *    RuffRuff renders and re-asserts its own label. Keeping the theme button
 *    enabled is also the fallback when RuffRuff does not recreate its clone
 *    after the theme's partial variant refresh.
 *    The theme never installs a watcher to revert RuffRuff's writes.
 * 2. Stock truth never overrides an in-effect rule. Whether an out-of-stock
 *    variant can be pre-ordered is decided by RuffRuff / Shopify, not the theme.
 * 3. Expired rules (past endsAt) are ignored and defer to Shopify stock state.
 * 4. For non-pre-order variants the theme renders ADD TO CART / SOLD OUT once;
 *    there is no third-party writer on those buttons so no watcher is needed.
 *
 * The button is server-rendered hidden+disabled and is only revealed once a
 * state has been confirmed, so a misleading label is never shown.
 */
(function () {
  if (window.theme && window.theme.cartButtonResolver) return;
  window.theme = window.theme || {};

  function getRuffruffData() {
    const el = document.getElementById('ruffruff-pre-order-product-json');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (_) {
      return null;
    }
  }

  function getRuleForVariant(ruffruffData, variantId) {
    if (!ruffruffData || !ruffruffData.variants || !variantId) return null;
    const entry = ruffruffData.variants[String(variantId)];
    return (entry && entry.preOrderRule) || null;
  }

  /**
   * Resolve a RuffRuff rule to a button state, but only while the rule is in
   * effect. Returns null for an expired rule so the caller falls back to the
   * normal stock-based state.
   * @returns {?{kind:string, disabled:boolean, label:string}}
   */
  function ruleState(rule, now, labels) {
    const startsAt = rule.startsAt ? Date.parse(rule.startsAt) : NaN;
    const endsAt = rule.endsAt ? Date.parse(rule.endsAt) : NaN;
    const beforeStart = Number.isFinite(startsAt) && now < startsAt;
    const afterEnd = Number.isFinite(endsAt) && now > endsAt;

    if (beforeStart) {
      return {
        kind: 'coming_soon',
        disabled: rule.actionsBeforeStartsAtEnabled === false,
        label: rule.buttonNameBeforeStartsAt || rule.buttonNameIfNotMet || labels.comingSoon
      };
    }
    if (!afterEnd) {
      // RuffRuff's `buttonCondition: "disable"` describes how the app treats
      // the original Shopify button before rendering its own enabled clone. It
      // does not mean that customers are blocked from ordering while the rule
      // is active. The clone is not always recreated after a partial variant
      // refresh, so the theme button itself must remain usable as the fallback.
      return {
        kind: 'preorder',
        disabled: false,
        label: rule.buttonName || labels.preorder
      };
    }
    return null;
  }

  /**
   * Pure resolver. No DOM access.
   * @param {object} ctx
   * @param {boolean} ctx.productAvailable - product.available (any variant in stock)
   * @param {?{id:string|number, available:boolean}} ctx.variant - selected variant
   * @param {boolean} ctx.hasPreorderSuffix - product.template_suffix contains 'preorder'
   * @param {?object} ctx.ruffruffData - parsed RuffRuff product JSON (may be null)
   * @param {number} ctx.now - Date.now()
   * @param {object} ctx.labels - { addToCart, preorder, noStock, noVariant, comingSoon }
   * @returns {{kind:string, disabled:boolean, label:string}}
   */
  function resolve(ctx) {
    const labels = ctx.labels || {};

    // An in-effect RuffRuff rule wins outright; stock truth does not override it.
    if (ctx.variant) {
      const rule = getRuleForVariant(ctx.ruffruffData, ctx.variant.id);
      if (rule) {
        const state = ruleState(rule, ctx.now, labels);
        if (state) return state;
      }
    }

    // Non-pre-order / expired-rule path: stock truth decides.
    if (!ctx.productAvailable) {
      return { kind: 'sold_out', disabled: true, label: labels.noStock };
    }
    if (!ctx.variant) {
      return { kind: 'no_variant', disabled: true, label: labels.noVariant };
    }
    if (ctx.variant.available === false) {
      return { kind: 'sold_out', disabled: true, label: labels.noStock };
    }
    if (ctx.hasPreorderSuffix) {
      return { kind: 'preorder', disabled: false, label: labels.preorder };
    }
    return { kind: 'add_to_cart', disabled: false, label: labels.addToCart };
  }

  /**
   * Apply a resolved state to a button. One-shot write: it reveals the button
   * and sets the label/disabled/kind once. It does NOT install any observer, so
   * RuffRuff (or any app) is free to re-render the button afterwards without the
   * theme fighting it.
   */
  function applyToButton(btnEl, state) {
    if (!btnEl || !state) return;
    btnEl.disabled = !!state.disabled;
    if (typeof state.label === 'string') {
      btnEl.textContent = state.label;
    }
    btnEl.dataset.cartButtonKind = state.kind;
    btnEl.style.visibility = '';
    btnEl.removeAttribute('aria-busy');

    const form = btnEl.closest('form');
    if (form) form.dataset.cartButtonKind = state.kind;
  }

  function labelsFromButton(btn) {
    return {
      addToCart: btn.dataset.addToCartText || '',
      preorder: btn.dataset.preorderText || '',
      noStock: btn.dataset.noStockText || '',
      noVariant: btn.dataset.noVariantText || btn.dataset.noStockText || '',
      comingSoon: btn.dataset.comingSoonText || ''
    };
  }

  /**
   * Best-effort initial resolve called by the inline boot script next to the
   * button. Confirms only states we can be certain about; otherwise leaves the
   * button hidden and schedules a retry.
   *
   * - in-effect RuffRuff rule  -> definitive, reveal PREORDER / COMING SOON now.
   * - variant.available === false (no rule) -> definitive, show SOLD OUT now.
   * - everything else (no rule visible yet) -> wait for DOMContentLoaded. By
   *   that point all server-rendered sections have been parsed; if RuffRuff
   *   JSON is anywhere in the document it is now visible to us. If it is still
   *   absent we accept that RuffRuff is not configured for this variant and
   *   resolve to ADD TO CART.
   * - Two setTimeout fallbacks cover the rare case of the app injecting its JSON
   *   after DOMContentLoaded via JS.
   */
  function bootButton(btn) {
    if (!btn) return;

    // If Liquid had no current_variant (manual variant picking mode), keep the
    // button hidden+disabled and let variant-picker.js handle the state when the
    // user picks an option.
    if (!btn.dataset.currentVariantId) return;

    const attemptResolve = function (allowAddToCart) {
      const productAvailable = btn.dataset.productAvailable === 'true';
      const variantId = btn.dataset.currentVariantId || '';
      const variantAvailable = btn.dataset.currentVariantAvailable === 'true';
      const hasPreorderSuffix = btn.dataset.hasPreorderSuffix === 'true';
      const variant = variantId ? { id: variantId, available: variantAvailable } : null;
      const ruffruffData = getRuffruffData();
      const now = Date.now();
      const labels = labelsFromButton(btn);
      const ctx = {
        productAvailable: productAvailable,
        variant: variant,
        hasPreorderSuffix: hasPreorderSuffix,
        ruffruffData: ruffruffData,
        now: now,
        labels: labels
      };

      // Definitive: an in-effect RuffRuff rule for the selected variant. Reveal
      // once and hand the button to RuffRuff.
      if (variant) {
        const rule = getRuleForVariant(ruffruffData, variant.id);
        if (rule && ruleState(rule, now, labels)) {
          applyToButton(btn, resolve(ctx));
          return true;
        }
      }

      // Definitive: sold-out / no-variant with no in-effect rule.
      if (!productAvailable || !variant || variantAvailable === false) {
        applyToButton(btn, resolve(ctx));
        return true;
      }

      // No rule visible. If we are still parsing HTML, RuffRuff JSON may appear
      // further down the document -- wait before committing ADD TO CART.
      if (!allowAddToCart) return false;

      applyToButton(btn, resolve(ctx));
      return true;
    };

    // Try once synchronously (cheap; only commits if definitive).
    if (attemptResolve(false)) return;

    // After full HTML parse, any remaining RuffRuff JSON is visible.
    const finalize = function () {
      if (btn.dataset.cartButtonKind) return; // already resolved
      attemptResolve(true);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', finalize, { once: true });
    } else {
      finalize();
    }

    // Defensive: a couple of retries for apps that inject JSON post-load.
    setTimeout(function () { if (!btn.dataset.cartButtonKind) attemptResolve(true); }, 250);
    setTimeout(function () { if (!btn.dataset.cartButtonKind) attemptResolve(true); }, 1000);
  }

  window.theme.cartButtonResolver = {
    resolve: resolve,
    applyToButton: applyToButton,
    getRuffruffData: getRuffruffData,
    getRuleForVariant: getRuleForVariant,
    labelsFromButton: labelsFromButton,
    bootButton: bootButton
  };
})();
